/**
 * Iter 67cx — Admin Locations panel.
 *
 * CRUD over the centralised location catalog. Supports filtering by
 * country / kind / active state, an inline edit + delete on each row,
 * a "+ New location" dialog, and a one-shot "Seed Balkan defaults"
 * button that calls the idempotent backend seed endpoint.
 *
 * Locations are SOFT-deleted (is_active=false) so cars already attached
 * by name don't break.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Save, Trash2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

const KIND_LABELS = {
  city: "City", airport: "Airport", port: "Port",
  hotel_area: "Hotel area", tourist: "Tourist destination",
};
const KIND_EMOJI = { city: "🏙️", airport: "✈️", port: "⛴️", tourist: "🏝️", hotel_area: "🏨" };
const ALL_KINDS = Object.keys(KIND_LABELS);
const ALL_COUNTRIES_SENTINEL = "__all__";
const ALL_KINDS_SENTINEL = "__all_kinds__";

export default function AdminLocationsPanel() {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = () => {
    setLoading(true);
    const params = {};
    if (country) params.country = country;
    if (kind) params.kind = kind;
    Promise.all([
      api.get("/admin/locations", { params }),
      api.get("/locations/catalog/countries"),
    ]).then(([r, c]) => {
      setRows(r.data || []);
      setCountries(c.data || []);
    }).catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [country, kind]);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/locations/seed");
      toast.success(`Seed complete · ${r.data.inserted} added · ${r.data.skipped} already present`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const counts = useMemo(() => {
    const m = { total: rows.length, active: 0, by_kind: {} };
    for (const r of rows) {
      if (r.is_active) m.active += 1;
      m.by_kind[r.kind] = (m.by_kind[r.kind] || 0) + 1;
    }
    return m;
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="admin-locations-panel">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h3 className="font-display text-xl font-semibold tracking-tight">Location catalog</h3>
          <p className="text-xs text-neutral-500">
            Single source of truth for the autocomplete on every search box.
            <span className="ml-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-neutral-600">
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800">{counts.active} active</span>
              <span className="px-1.5 py-0.5 rounded-md bg-neutral-100">{counts.total} total</span>
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={country || ALL_COUNTRIES_SENTINEL} onValueChange={(v) => setCountry(v === ALL_COUNTRIES_SENTINEL ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="admin-locations-country-filter"><SelectValue placeholder="All countries" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_COUNTRIES_SENTINEL}>All countries</SelectItem>
              {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={kind || ALL_KINDS_SENTINEL} onValueChange={(v) => setKind(v === ALL_KINDS_SENTINEL ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="admin-locations-kind-filter"><SelectValue placeholder="All kinds" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_KINDS_SENTINEL}>All kinds</SelectItem>
              {ALL_KINDS.map((k) => <SelectItem key={k} value={k}>{KIND_EMOJI[k]} {KIND_LABELS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={refresh} variant="outline" size="sm" className="rounded-full"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
          <Button onClick={seed} disabled={seeding} variant="outline" size="sm" className="rounded-full" data-testid="admin-locations-seed">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />{seeding ? "Seeding…" : "Seed Balkan defaults"}
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-sky-600 hover:bg-sky-700" data-testid="admin-locations-create">
                <Plus className="h-3.5 w-3.5 mr-1.5" />New location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New location</DialogTitle>
                <DialogDescription>
                  Add a city, airport, port, hotel area, or tourist destination. The slug + SEO landing page hook are generated automatically.
                </DialogDescription>
              </DialogHeader>
              <_CreateForm onCreated={() => { setCreateOpen(false); refresh(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center" data-testid="admin-locations-empty">
          <div className="text-sm font-semibold text-neutral-700 mb-1">No locations match</div>
          <div className="text-xs text-neutral-500">Try a different filter, or click "Seed Balkan defaults" to bootstrap 100+ entries.</div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50/60 text-[10px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2.5 font-bold">Name</th>
                  <th className="text-left px-3 py-2.5 font-bold">Country</th>
                  <th className="text-left px-3 py-2.5 font-bold">Kind</th>
                  <th className="text-left px-3 py-2.5 font-bold">Aliases</th>
                  <th className="text-left px-3 py-2.5 font-bold">Slug</th>
                  <th className="text-left px-3 py-2.5 font-bold">Status</th>
                  <th className="text-right px-3 py-2.5 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => <_Row key={r.id} row={r} onChanged={refresh} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function _CreateForm({ onCreated }) {
  const [form, setForm] = useState({ name: "", country: "", country_code: "", kind: "city", aliases: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.country.trim()) {
      toast.error("Name + country are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/locations", {
        name: form.name.trim(),
        country: form.country.trim(),
        country_code: form.country_code.trim() || null,
        kind: form.kind,
        aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Location added");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3 mt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tirana Airport" data-testid="admin-locations-form-name" />
        </div>
        <div>
          <Label className="text-xs">Country</Label>
          <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Albania" data-testid="admin-locations-form-country" />
        </div>
        <div>
          <Label className="text-xs">Country code (ISO-2)</Label>
          <Input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })} placeholder="AL" data-testid="admin-locations-form-cc" />
        </div>
        <div>
          <Label className="text-xs">Kind</Label>
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="admin-locations-form-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALL_KINDS.map((k) => <SelectItem key={k} value={k}>{KIND_EMOJI[k]} {KIND_LABELS[k]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Aliases (comma-separated)</Label>
          <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="TIA, Rinas, Nënë Tereza" data-testid="admin-locations-form-aliases" />
        </div>
      </div>
      <Button onClick={submit} disabled={saving} className="rounded-full bg-sky-600 hover:bg-sky-700" data-testid="admin-locations-form-submit">
        {saving ? "Saving…" : "Add location"}
      </Button>
    </div>
  );
}

function _Row({ row, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({
    name: row.name,
    country: row.country,
    kind: row.kind,
    aliases: (row.aliases || []).join(", "),
    is_active: row.is_active,
  });
  const save = async () => {
    try {
      await api.put(`/admin/locations/${row.id}`, {
        name: draft.name.trim(),
        country: draft.country.trim(),
        kind: draft.kind,
        aliases: draft.aliases.split(",").map((s) => s.trim()).filter(Boolean),
        is_active: draft.is_active,
      });
      toast.success("Saved");
      setEdit(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const toggleActive = async () => {
    try {
      await api.put(`/admin/locations/${row.id}`, { is_active: !row.is_active });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/locations/${row.id}`);
      toast.success("Location hidden");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (edit) {
    return (
      <tr className="border-t border-border bg-sky-50/40" data-testid={`admin-location-edit-${row.id}`}>
        <td className="px-3 py-2"><Input className="h-8 text-xs" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
        <td className="px-3 py-2"><Input className="h-8 text-xs" value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} /></td>
        <td className="px-3 py-2">
          <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_KINDS.map((k) => <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>)}</SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2" colSpan={2}><Input className="h-8 text-xs" value={draft.aliases} onChange={(e) => setDraft({ ...draft, aliases: e.target.value })} /></td>
        <td className="px-3 py-2"><Button size="sm" variant="outline" className="h-8 rounded-full" onClick={toggleActive}>{draft.is_active ? "Active" : "Hidden"}</Button></td>
        <td className="px-3 py-2 text-right">
          <Button size="sm" onClick={save} className="h-8 rounded-full bg-sky-600 hover:bg-sky-700 mr-1"><Save className="h-3 w-3" /></Button>
          <Button size="sm" variant="outline" onClick={() => setEdit(false)} className="h-8 rounded-full">Cancel</Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-border" data-testid={`admin-location-row-${row.id}`}>
      <td className="px-3 py-2 font-semibold text-neutral-900">{KIND_EMOJI[row.kind]} {row.name}</td>
      <td className="px-3 py-2 text-xs text-neutral-700">{row.country}{row.country_code ? <span className="text-neutral-400"> · {row.country_code}</span> : null}</td>
      <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded-md bg-neutral-100 text-neutral-700 text-[10px] uppercase tracking-wider font-bold">{KIND_LABELS[row.kind] || row.kind}</span></td>
      <td className="px-3 py-2 text-[11px] text-neutral-500 max-w-[180px] truncate" title={(row.aliases || []).join(", ")}>{(row.aliases || []).join(", ") || "—"}</td>
      <td className="px-3 py-2 text-[10px] text-neutral-400 font-mono">{row.slug}</td>
      <td className="px-3 py-2">
        <button type="button" onClick={toggleActive} className={`inline-flex items-center px-2 py-0.5 rounded-full ring-1 text-[10px] font-bold uppercase tracking-wider transition ${
          row.is_active ? "bg-emerald-100 text-emerald-800 ring-emerald-200 hover:bg-emerald-200" : "bg-neutral-100 text-neutral-500 ring-neutral-200 hover:bg-neutral-200"
        }`} data-testid={`admin-location-toggle-${row.id}`}>
          {row.is_active ? "Active" : "Hidden"}
        </button>
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button variant="outline" size="sm" className="h-8 rounded-full mr-1" onClick={() => setEdit(true)} data-testid={`admin-location-edit-btn-${row.id}`}>Edit</Button>
        <Button variant="outline" size="sm" className="h-8 rounded-full" onClick={remove} data-testid={`admin-location-delete-${row.id}`}><Trash2 className="h-3 w-3" /></Button>
      </td>
    </tr>
  );
}
